import React, { useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { DataTypeProvider } from '@devexpress/dx-react-grid';
import columns from '../columnDefinition';
import BooleanFormatter from './AllUserTable/services/booleanFormater';
import BooleanEditor from './components/BooleanEditor';
import commitChanges from './commitChanges';
import TextEditor from './components/TextEditor';
import createUserQuery from '../../../../Apollo/mutations/createUser';
import getAllUser from '../../../../Apollo/queries/getAllUser';
import PrmUserInit from '../../../../types/initValues/PrmUser.init';
import type { PrmUser } from '../../../../types/PrmUser.types';
import Table from '../../../../components/Table';
import updateUserQuery from '../../../../Apollo/mutations/updateUser';
import PasswordEditor from './components/PasswordEditor';
import PasswordFormater from './AllUserTable/services/PasswordFormater';
import RolesEditor from './components/RolesEditor';
import AlertMessage from '../../../../components/AlertMessage';

const booleanColumns = ['enabled'];

function BooleanTypeProvider() {
  return (
    <DataTypeProvider formatterComponent={BooleanFormatter} editorComponent={BooleanEditor} for={booleanColumns} />
  );
}

const textColumns = ['username', 'email'];

function TextTypeProvider() {
  return <DataTypeProvider editorComponent={TextEditor} for={textColumns} />;
}

const passwordColumns = ['password'];

function PasswordProvider() {
  return (
    <DataTypeProvider formatterComponent={PasswordFormater} editorComponent={PasswordEditor} for={passwordColumns} />
  );
}

const rolesColums = ['roles'];

function RolesProvider() {
  return <DataTypeProvider for={rolesColums} editorComponent={RolesEditor} />;
}

const Provider = [BooleanTypeProvider, TextTypeProvider, PasswordProvider, RolesProvider];

// CONTRACT: Exportierter Query-Daten-Typ — pinnt die Apollo-Boundary an der UserTable useQuery-Stelle.
// Exportiert fuer Compile-Time-Contract-Tests in user-password-hash-removal.spec.ts.
// Aenderungen (z.B. id?: number, any) brechen dort die Compile-Time-Assertions.
export type UserTableGetAllUserQueryData = { getAllUser: PrmUser[] };

function UserTable() {
  // Merge RITA-1314: master brachte die reset-/handleCloseAlert-Logik, das Epic (RITA-1376)
  // die Typisierung samt Wiring-Guard. Beides bleibt erhalten.
  const [createUser, { data: dataCreateUser, error: errorCreateUser, reset: resetCreateUser }] =
    useMutation(createUserQuery);
  const [updateUser, { data: dataUpdateUser, error: errorUpdateUser, reset: resetUpdateUser }] =
    useMutation(updateUserQuery);
  // CONTRACT: useQuery typisiert — id ist required (number, kein optional). Erzwingt PrmUser.id-Invariante an der Apollo-Grenze.
  // UserTableGetAllUserQueryData ist exportiert — Typ-Aenderung hier bricht Contract-Test.
  const { data: Users, refetch } = useQuery<UserTableGetAllUserQueryData>(getAllUser);
  // CONTRACT: Wiring-Guard — useQuery ist NICHT useQuery<any>.
  // Schlaegt fehl wenn auf useQuery<any> umverdrahtet wird (1&any=any, 0 extends any=true → never).
  // Schlaegt NICHT fehl wenn useQuery<UserTableGetAllUserQueryData> (korrekte Typisierung).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const userTableQueryWiringGuard: 0 extends 1 & typeof Users ? never : true = true;

  const handleCloseAlert = useCallback(() => {
    resetCreateUser();
    resetUpdateUser();
  }, [resetCreateUser, resetUpdateUser]);

  const editingStateColumnExtensions = [
    { columnName: 'id', editingEnabled: false },
    { columnName: 'last_login', editingEnabled: false },
  ];

  return (
    <>
      <Table
        // CONTRACT: getAllUser null/undefined → [PrmUserInit] als Platzhalter bis Daten geladen.
        // ?? statt ternary, damit auch { getAllUser: null } und { getAllUser: undefined } korrekt fallen.
        row={Users?.getAllUser ?? [PrmUserInit]}
        columns={columns}
        defaultColumnWidths={columns.map((column) => ({ columnName: column.name, width: 'auto' }))}
        // CONTRACT: rows für commitChanges ?? [PrmUserInit] — spiegelt row-Prop exakt.
        // ?? [] würde 'changed'-Events auf den Platzhalter lautlos verwerfen: rows.map([])
        // findet keinen Match, updateUser() wird nie aufgerufen (Silent-Drop).
        // ?? [PrmUserInit] macht commitChanges konsistent mit dem Display-State (row-Prop);
        // commitChanges.js setzt Array voraus (rows.length, rows.map) — Fallback verhindert Crash.
        // PrmUserInit.id = 0 (Sentinel) stellt sicher, dass im add-Pfad startingAddedId = 0 + 1 = 1
        // statt undefined + 1 = NaN berechnet wird, auch wenn showAddCommand bei leerem Zustand aktiv ist.
        onCommitChanges={({ added, changed }) =>
          commitChanges({ added, changed, rows: Users?.getAllUser ?? [PrmUserInit], refetch, createUser, updateUser })
        }
        columnExtensions={editingStateColumnExtensions}
        dataTypeProvider={Provider}
        editable
        showAddCommand
        // CONTRACT: showEditCommand nur wenn echte User-Daten vorhanden — PrmUserInit-Platzhalter
        // ist nicht editierbar (würde updateUser({username:''}) auf Zeile mit username='' senden).
        showEditCommand={!!(Users?.getAllUser && Users.getAllUser.length > 0)}
      />
      <AlertMessage
        typeMessage="success"
        alertMessage={
          (dataUpdateUser && !errorUpdateUser && 'Benutzerdaten erfolgreich geändert') ||
          (dataCreateUser && !errorCreateUser && 'Benutzer wurde erfolgreich erstellt') ||
          undefined
        }
        isAlertMessage={!!(dataUpdateUser && !errorUpdateUser) || !!(dataCreateUser && !errorCreateUser)}
        handleChangeIsAlertMessage={handleCloseAlert}
      />
    </>
  );
}

export default UserTable;
